import React from 'react';
import { Modal, Typography, Card } from 'antd';
import moment from 'moment';
// Helpers
import { parseForInnerHTML } from 'helpers/textEditor';
import { getDeliverableItemMedia } from 'helpers/timelineDeliverables';
// Types
import {
  GetCampaignOffers_getCampaignOffers_entities_deliverablesV2,
  GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_revisions,
  GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_revisions_items,
} from 'api/campaignV2/types/GetCampaignOffers';
import {
  CBDeliverableItemType,
  CbDeliverableItemV2Status,
} from 'api/graphql-global-types';
// Styles
import styles from './RevisionHistoryModal.module.scss';

const { Text, Title } = Typography;

type RevisionHistoryModalProps = {
  deliverable: GetCampaignOffers_getCampaignOffers_entities_deliverablesV2;
  isOpen: boolean;
  onClose: () => void;
};

const RevisionHistoryModal: React.FC<RevisionHistoryModalProps> = ({
  deliverable,
  isOpen,
  onClose,
}) => {
  const renderRevisionItem = (
    revision: GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_revisions_items
  ) => {
    const currentItem = deliverable.items.find((i) => i.id === revision.itemId);
    const isItemApproved =
      currentItem?.status === CbDeliverableItemV2Status.APPROVED;
    const isFile = revision.type === CBDeliverableItemType.FILE;
    const isFiles = revision.type === CBDeliverableItemType.FILES;
    const isMediaItem = isFile || isFiles;
    const { url, originalUrl } = getDeliverableItemMedia(currentItem);
    const downloadUrl = originalUrl || url;

    return (
      <div key={revision.id} className={styles.revisionItem}>
        <div className={styles.revisionItemHeader}>
          <Text strong>
            {isFiles
              ? 'Final Content'
              : isFile
              ? 'Content Item'
              : 'Post Caption'}
          </Text>
          <Text> - {revision.taskTitle}</Text>
        </div>

        <div className={styles.status}>
          <Text>Status: </Text>
          <Text
            strong
            className={
              isItemApproved
                ? styles.statusApproved
                : styles.statusChangesNeeded
            }
          >
            {isItemApproved ? 'Approved' : 'Changes Needed'}
          </Text>
        </div>

        <div className={styles.submittedContentContainer}>
          <Text>
            {isFiles
              ? 'Submitted Final Content: '
              : isFile
              ? 'Submitted Content: '
              : 'Submitted Post Caption: '}
          </Text>
          <Text className={styles.submittedContent}>
            {isMediaItem ? (
              <a href={downloadUrl} target="_blank" rel="noopener noreferrer">
                Download File
              </a>
            ) : (
              <span
                className={styles.copyText}
                dangerouslySetInnerHTML={{
                  __html: parseForInnerHTML(
                    currentItem && 'copy' in currentItem
                      ? currentItem.copy
                      : 'No caption provided'
                  ),
                }}
              />
            )}
          </Text>
        </div>

        {revision.feedback && (
          <div className={styles.feedbackSection}>
            <Text strong className={styles.feedbackLabel}>
              Feedback:
            </Text>
            <div className={styles.feedbackContent}>{revision.feedback}</div>
          </div>
        )}
      </div>
    );
  };

  const renderRevision = (
    revision: GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_revisions,
    index: number
  ) => {
    return (
      <Card key={revision.id} className={styles.revisionCard}>
        <div className={styles.revisionHeader}>
          <Title level={5} className={styles.revisionTitle}>
            {index === 0 ? 'Original Submission' : `Revision #${index}`}
          </Title>
          <div className={styles.revisionMeta}>
            <Text type="secondary">
              {moment(revision.createdAt).format('MM/DD/YYYY HH:mm')}
            </Text>
            {revision.performedByEmail && (
              <Text type="secondary">by {revision.performedByEmail}</Text>
            )}
          </div>
        </div>

        <div className={styles.revisionItems}>
          {revision.items.map(renderRevisionItem)}
        </div>
      </Card>
    );
  };

  const hasRevisions =
    deliverable.revisions && deliverable.revisions.length > 0;

  return (
    <Modal
      title="Revision History"
      visible={isOpen}
      onCancel={onClose}
      footer={null}
      width="800px"
      className={styles.modal}
    >
      <div className={styles.container}>
        {hasRevisions ? (
          <div className={styles.revisionsList}>
            {deliverable.revisions.map(renderRevision)}
          </div>
        ) : (
          <div className={styles.noRevisions}>
            <Text type="secondary">No revision history available</Text>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default RevisionHistoryModal;
